import axios, { AxiosError } from 'axios';
import { useEffect, useState, useCallback } from 'react';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CryptoJS from 'crypto-js';

// config
import { wqkgx } from '../../config/keys';

// images
import { arrowRight, eyes } from '../../assets/images/login';

// components
import OtpModal from '../../components/global/OtpModal';
import SuccessModal from '../../components/global/SuccessModal';
import UnVerifiedPhoneModal from '../../components/global/UnVerifiedPhoneModal';

// utils
import { Colors, baseUrl_, timeout } from '../../utils/constants';
import { handleError } from '../../utils/errorHandler';
import { isEmailValid } from '../../utils/validateEmail';
// import { validateEmail } from '../../lib/validateEmail';

// css
import styles from './loginForm.module.css';
import { encryptData } from '../../utils/encryptJson';
import { decryptJson } from '../../utils/decryptJson';
import useOnSuccessfulLogin from '../../hooks/useOnSuccessfulLogin';
import { registerNetcoreActivity } from '../../utils/registerActivity';
import ReactGA from 'react-ga4';
import Typography from '../../components_/Typography';
import { Box, Flex, Img, Input, Text } from '@chakra-ui/react';
import Button from '../../components_/button';
import customToast, { ToastType } from '../../components_/Toast';
import { Request } from '../../apis/request';

const LoginForm = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isDisabled, setIsDisabled] = useState(true);
  const [tokenSent, setTokenSent] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [showUnverifiedPhoneModal, setShowUnverifiedPhoneModal] = useState(false);
  const [seePassword, setSeePassword] = useState(false);
  const { successfulLoginAction } = useOnSuccessfulLogin();
  const passwordLength = CryptoJS.AES.decrypt(password, wqkgx).sigBytes;

  useEffect(() => {
    if (passwordLength >= 8 && passwordLength <= 50 && isEmailValid(email)) {
      setIsDisabled(false);
    } else {
      setIsDisabled(true);
    }
  }, [email, password]);

  //   LOG IN ACTIONS //
  type TLogin = {
    email: string;
    password: string;
  };

  const errorFn = (error: AxiosError<{ message: string }>) => {
    if (error?.response?.data?.message) {
      customToast(error.response.data.message, ToastType.error);
    } else {
      customToast('Something went wrong', ToastType.error);
    }
  };
  const loginUser = useMutation(
    (data: TLogin) =>
      Request.post(`/auth/login`, encryptData(data), {
        headers: {
          sessId: localStorage.getItem('sessId') ?? '',
        },
      }),
    {
      onSuccess: (res: any) => {
        const response = decryptJson(res?.data?.data);
        if (Object.keys(response)?.length && !response.is2FAEnabled) {
          successfulLoginAction({
            token: response.token ?? '',
            userDetails: response,
            successFn: () => {
              customToast('Login successful', ToastType.success);
            },
            verifyPhoneFn: () => {
              setShowUnverifiedPhoneModal(true);
            },
          });
        } else {
          setEmailOtpSent(true);
        }
        registerNetcoreActivity(
          {
            firstName: response?.firstName,
            lastName: response?.lastName,
            emailAddress: response?.email,
            mobileNumber: response?.phoneNumber,
          },
          'TBH_LOGIN_STEP_2',
        );

        ReactGA.event({
          category: 'Button',
          action: 'TBH_LOGIN_STEP_2',
          label: 'TBH_LOGIN_STEP_2',
          // value: 1
        });
      },
      onError: (error: AxiosError<{ message: string }>) => {
        if (error.response?.status === 400) {
          const response = error.response?.config?.data;
          //   console.log('response', response);
          //   registerNetcoreActivity(
          //       {
          //           firstName: response?.firstName,
          //           lastName: response?.lastName,
          //           emailAddress: response?.email,
          //           mobileNumber: response?.phoneNumber,
          //       },
          //       'Incorrect password',
          //   );
        }
        handleError({ error, errorFn });
      },
    },
  );

  const onclickLogin = useCallback(
    (data: TLogin) => {
      !loginUser.isLoading && loginUser.mutate(data);
    },
    [loginUser],
  );

  useEffect(() => {
    const keyDownHandler = (event: { key: string; preventDefault: () => void }) => {
      if (event.key === 'Enter') {
        event.preventDefault();

        // 👇️ call submit function here
        onclickLogin({ email, password });
        setIsDisabled(true);
      }
    };

    document.addEventListener('keydown', keyDownHandler);

    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, [email, onclickLogin, password]);

  return (
    <div className={styles.formContainer}>
      <div className='mb-3'>
        <h3 className={`${styles.loginText} font-Poppins font-semibold`} color={Colors.black}>
          Welcome back
        </h3>
        <Typography color={Colors.black} fontSize={'0.75rem'} fontWeight={400}>
          Login to your account
        </Typography>
      </div>

      <Flex mt={'3rem'} gap={'0.13rem'} flexDirection={{ base: 'column', md: 'row' }}>
        <Typography color={Colors.black} fontSize={'0.875rem'} fontWeight={400}>
          New user?
        </Typography>
        <Flex>
          <Typography
            color={Colors.green}
            fontSize={{ base: '0.675rem', md: '0.875rem' }}
            fontWeight={500}
            onClick={() => navigate('/signup')}
            cursor={'pointer'}
          >
            Sign up for a free account
          </Typography>
          <Img src={arrowRight} ml={'0.25rem'} />
        </Flex>
      </Flex>

      <Input
        borderRadius={'0.25rem'}
        border={`0.5px solid ${Colors.secondaryGreen300}`}
        placeholder='Email address'
        type={'email'}
        fontSize={'0.75rem'}
        fontWeight={400}
        flex={1}
        height={'2.8125rem'}
        onChange={(e) => setEmail(e.target.value.toLowerCase())}
        _placeholder={{
          mt: '-10px',
        }}
        value={email}
        px={{ base: '0.75rem', md: '1rem' }}
        mb={'1rem'}
        mt={'1.5rem'}
        fontFamily={'Poppins'}
        _focusVisible={{
          outlineColor: 'none',
          border: `2px solid ${Colors.green}`,
        }}
      />

      <div className='relative'>
        <Input
          borderRadius={'0.25rem'}
          border={`0.5px solid ${Colors.secondaryGreen300}`}
          placeholder='Password'
          type={!seePassword ? 'password' : 'text'}
          fontSize={'0.75rem'}
          fontWeight={400}
          flex={1}
          height={'2.8125rem'}
          onChange={(e) => setPassword(CryptoJS.AES.encrypt(e.target.value, wqkgx).toString())}
          _placeholder={{
            mt: '-10px',
          }}
          // value={password}
          px={{ base: '0.75rem', md: '1rem' }}
          fontFamily={'Poppins'}
          _focusVisible={{
            outlineColor: 'none',
            border: `2px solid ${Colors.green}`,
          }}
        />
        <img
          src={eyes}
          alt='see_password.png'
          className='absolute bottom-4 right-6 cursor-pointer'
          onClick={() => {
            setSeePassword(!seePassword);
          }}
        />
      </div>

      <Box
        style={{
          fontSize: '11px',
          lineHeight: '16.5px',
        }}
        className=' font-Poppins font-medium text-[#229698] cursor-pointer'
        onClick={() => navigate('/forgot-password')}
        mb={'2rem'}
        mt={'0.5rem'}
      >
        Forgot Password?
      </Box>

      <div className='flex justify-center'>
        <Button
          width={'100%'}
          disabled={isDisabled}
          onClick={() => {
            registerNetcoreActivity(
              {
                emailAddress: email,
              },
              'TBH_LOGIN_SUCCESSFUL',
            );
            ReactGA.event({
              category: 'Button',
              action: 'TBH_LOGIN_SUCCESSFUL',
              label: 'TBH_LOGIN_SUCCESSFUL',
              // value: 1
            });
            onclickLogin({ email, password });
          }}
          py={{ base: '0.75rem', md: '1rem' }}
          height={{ base: '2.5rem', md: '2.875rem' }}
          text={'Login'}
          type='submit'
          isLoading={loginUser.isLoading}
          zIndex={1000}
        />
      </div>

      <OtpModal
        pageComingFrom={'Login'}
        showModal={tokenSent}
        setShowModal={setTokenSent}
        setShowSuccessModal={setShowSuccessModal}
      />

      <UnVerifiedPhoneModal
        setShowModal={setShowUnverifiedPhoneModal}
        showModal={showUnverifiedPhoneModal}
        showPhoneOtpModal={() => setTokenSent(true)}
        closeEmailOtpModal={() => setEmailOtpSent(false)}
      />

      <OtpModal
        email={email?.toLowerCase()}
        pageComingFrom={'Login'}
        showModal={emailOtpSent}
        setShowModal={setEmailOtpSent}
        setShowSuccessModal={setShowSuccessModal}
        showUnverifiedPhoneModal={() => setShowUnverifiedPhoneModal(true)}
      />

      <SuccessModal
        showSuccessModal={showSuccessModal}
        setShowSuccessModal={setShowSuccessModal}
        pageComingFrom={'Login'}
        closePreviousModal={() => setTokenSent(false)}
      />
    </div>
  );
};

export default LoginForm;
